import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const SeverePsychiatricDisorders = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [symptomsCanScrollLeft, setSymptomsCanScrollLeft] = useState(false);
  const [symptomsCanScrollRight, setSymptomsCanScrollRight] = useState(true);
  const [causesCanScrollLeft, setCausesCanScrollLeft] = useState(false);
  const [causesCanScrollRight, setCausesCanScrollRight] = useState(true);
  const [typesCanScrollLeft, setTypesCanScrollLeft] = useState(false);
  const [typesCanScrollRight, setTypesCanScrollRight] = useState(true);
  const [treatmentCanScrollLeft, setTreatmentCanScrollLeft] = useState(false);
  const [treatmentCanScrollRight, setTreatmentCanScrollRight] = useState(true);
  const symptomsScrollRef = useRef(null);
  const causesScrollRef = useRef(null);
  const typesScrollRef = useRef(null);
  const treatmentScrollRef = useRef(null);

  // Scroll detection for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'symptoms', 'causes', 'types', 'treatment'];
      const scrollPosition = window.scrollY + 250; // Offset for better detection

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filterContent = (section) => {
    setActiveSection(section);
  };

  const checkScrollPosition = (ref) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      const canScrollLeft = scrollLeft > 0;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
      return { canScrollLeft, canScrollRight };
    }
    return { canScrollLeft: false, canScrollRight: false };
  };

  const scrollSymptoms = (direction) => {
    if (symptomsScrollRef.current) {
      const scrollAmount = 320; // Card width + gap
      symptomsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(symptomsScrollRef);
        setSymptomsCanScrollLeft(position.canScrollLeft);
        setSymptomsCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollCauses = (direction) => {
    if (causesScrollRef.current) {
      const scrollAmount = 320;
      causesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(causesScrollRef);
        setCausesCanScrollLeft(position.canScrollLeft);
        setCausesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollTypes = (direction) => {
    if (typesScrollRef.current) {
      const scrollAmount = 320;
      typesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(typesScrollRef);
        setTypesCanScrollLeft(position.canScrollLeft);
        setTypesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollTreatment = (direction) => {
    if (treatmentScrollRef.current) {
      const scrollAmount = 320;
      treatmentScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(treatmentScrollRef);
        setTreatmentCanScrollLeft(position.canScrollLeft);
        setTreatmentCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#E6F7F4]"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
          <div className="relative overflow-hidden rounded-3xl bg-[#E6F7F4]">
            <div className="relative px-6 py-24 sm:px-10 sm:py-32 lg:px-12 lg:py-36 text-center">
              <img
                src="/static/img/services/severe.png"
                alt=""
                className="pointer-events-none absolute -right-0 top-1/2 hidden w-80 -translate-y-1/2 opacity-75 lg:block"
              />

              <div className="mx-auto flex items-center justify-center gap-2 text-xs tracking-wide text-gray-700">
                <span>Adult Services</span>
              </div>

              <h1 className="mt-6 font-extrabold tracking-tight text-gray-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
                Severe Psychiatric <br/>Disorders Care
              </h1>

              <p className="mt-6 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Comprehensive care for severe mental health conditions. Specialized treatment, medication management, and intensive support for complex psychiatric disorders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Action Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center py-4">
            <div className="flex flex-wrap items-center gap-6">
              <a 
                href="#overview" 
                className={`font-medium transition-colors ${
                  activeSection === 'overview' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('overview');
                  const offset = 200; // Height of main navbar + action bar + extra spacing + padding
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Overview
              </a>
              <a 
                href="#symptoms" 
                className={`font-medium transition-colors ${
                  activeSection === 'symptoms' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('symptoms');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Symptoms
              </a>
              <a 
                href="#causes" 
                className={`font-medium transition-colors ${
                  activeSection === 'causes' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('causes');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Causes and Risk
              </a>
              <a 
                href="#types" 
                className={`font-medium transition-colors ${
                  activeSection === 'types' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('types');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Types
              </a>
              <a 
                href="#treatment" 
                className={`font-medium transition-colors ${
                  activeSection === 'treatment' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('treatment');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Treatment
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Overview */}
          <div id="overview" className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Overview</h2>
            <p className="text-lg text-gray-600 mb-8">
              Severe psychiatric disorders refer to complex mental health issues that affect an individual's thinking, feeling, and behaving. 
              These mental health issues often require long-term treatment and care to ensure the mental health patient can effectively manage their symptoms.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Severe psychiatric disorders often touch on all aspects of life, including social interactions, working, and personal care. 
              With the right treatment, care, and support, mental health patients can be able to manage their symptoms and live a full life.
            </p>
          </div>
        </div>

        {/* Symptoms Section with Full-Width Background */}
        <div id="symptoms" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Symptoms</h2>
            
            {/* Common Signs */}
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Recognizing signs of severe psychiatric disorders</h3>
              
              {/* Scroll Container */}
              <div 
                ref={symptomsScrollRef}
                className="overflow-x-auto scrollbar-hide mb-4"
              >
                <div className="flex space-x-4 py-4 w-fit">
                  {[
                    {
                      symptom: "Hallucinations",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Delusions",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Disorganized Thinking/Speech",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Extreme High Energy or Over Confidence",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Severe Depression",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Rapid Mood Swings",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Social Isolation and Loss of Interest in Activities",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Neglect Of Personal Care",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Disorganized Or Unusual Behavior",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Difficulty Concentrating, Remembering, or Thinking Logically",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-[300px]">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">{item.symptom}</h4>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-center items-center space-x-4">
                <button
                  onClick={() => scrollSymptoms('left')}
                  disabled={!symptomsCanScrollLeft}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollLeft 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => scrollSymptoms('right')}
                  disabled={!symptomsCanScrollRight}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollRight 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hide Scrollbar Styles */}
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

      {/* Causes Section with Full-Width Background */}
      <div id="causes" className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Causes and Risk Factors</h2>
          <h3 className="text-lg font-normal text-gray-700 mb-6">Understanding factors contributing to severe psychiatric disorders</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Genetic Factors",
                description: "Family history and genetics have been known to influence the development of severe psychiatric illnesses.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )
              },
              {
                title: "Brain Chemistry",
                description: "Chemical imbalances in the brain, such as abnormalities in the structure of the brain, can lead to the development of severe mental illnesses.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                )
              },
              {
                title: "Environmental Factors",
                description: "Trauma, stress, abuse, and major life events can lead to the development of severe mental illnesses.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )
              },
              {
                title: "Substance Abuse",
                description: "Substance abuse, including the use of drugs and alcohol, can lead to the development of severe mental illnesses.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                )
              },
              {
                title: "Chronic Stress",
                description: "Severe mental illnesses can be triggered by stress, major life challenges, or traumatic events.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              }
            ].map((item, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">{item.icon}</div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

        {/* Types Section with Full-Width Background */}
        <div id="types" className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Types of Severe Psychiatric Disorders</h2>
            
            <div>
              <h3 className="text-lg font-normal text-gray-600 mb-6">Major categories of severe mental illness</h3>
              
              {/* Single Row Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: "Schizophrenia",
                    description: "A chronic disorder affecting thinking, feeling, and behavior, often involving psychosis and distorted reality.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )
                  },
                  {
                    title: "Bipolar Disorder",
                    description: "Characterized by extreme mood swings ranging from depressive lows to manic highs.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                    )
                  },
                  {
                    title: "Major Depressive Disorder",
                    description: "Persistent feelings of sadness, hopelessness, and loss of interest that significantly impact daily life.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Schizoaffective Disorder",
                    description: "A combination of schizophrenia symptoms and mood disorder symptoms.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )
                  }
                ].map((type, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {type.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{type.title}</h4>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Treatment Section with Full-Width Background */}
        <div id="treatment" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Treatments</h2>
            
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Comprehensive approaches to managing severe psychiatric disorders</h3>
              
              {/* Single Row Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: "Medication Management",
                    description: "Antipsychotics, mood stabilizers, and antidepressants assist in managing symptoms and relapse.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                    )
                  },
                  {
                    title: "Psychotherapy",
                    description: "Individual and group therapy assist in the development of skills to effectively manage symptoms.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    )
                  },
                  {
                    title: "Hospitalization",
                    description: "Inpatient treatment is used to assist during an acute crisis or when symptoms are severe.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )
                  },
                  {
                    title: "Brain Stimulation Therapy",
                    description: "Electroconvulsive therapy (ECT) or Transcranial Magnetic Stimulation (TMS) is used for severe and resistant depression or mania.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    )
                  }
                ].map((treatment, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {treatment.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{treatment.title}</h4>
                        <p className="text-sm text-gray-600">{treatment.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeverePsychiatricDisorders;
